import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import { APP_CONFIG } from "../constants/constants";
import Select from "react-select";
import { TSelectboxOption } from "./admin/formFields";
import { useTheme } from "./common/ThemeContext";

export type TPaginationSelected = {
  selected: number;
};

type TPaginatonProps = {
  pageSizeOptions?: number[];
  pageSize: number;
  pageRangeDisplayed?: number;
  marginPagesDisplayed?: number;
  // handlePageSizeChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handlePageSizeChange?: (e: TSelectboxOption | null) => void;
  totalPages?: number;
  forcePage: number;
  handlePageClick: (data: TPaginationSelected) => void;
  onlyPageChange?: boolean;
  size?: 1 | 2;
  paginCurrentPage?: number;
  paginItemsOnCurrentPage?: number;
  paginTotalItemsCount?: number;
  wrapperClass?: string;
  isLoading?: boolean;
  resultCountPrefixAtBottomLeft?: string | boolean;
};

const Pagination: FC<TPaginatonProps> = ({
  pageSizeOptions = APP_CONFIG.LISTINGS.PAGE_SIZES,
  pageSize = APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
  pageRangeDisplayed = 2,
  marginPagesDisplayed = 2,
  handlePageSizeChange,
  totalPages = 1,
  forcePage,
  handlePageClick,
  onlyPageChange = false,
  size = 2,
  paginCurrentPage = 1,
  paginItemsOnCurrentPage = 0,
  paginTotalItemsCount = 0,
  wrapperClass = `flex mt-3 items-center`,
  isLoading = false,
  resultCountPrefixAtBottomLeft = false,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "pagination" });
  const { colors } = useTheme();
  return (
    <div
      className={`${wrapperClass}${isLoading ? " opacity-50 pointer-events-none touch-none" : ""} ${onlyPageChange ? "justify-center" : "justify-between"}`}
    >
      {onlyPageChange ? (
        <></>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row w-56 space-y-1 lg:space-x-4 lg:items-center">
            {!!resultCountPrefixAtBottomLeft ? (
              <p className="font-semibold text-accent-blue-dark">
                {resultCountPrefixAtBottomLeft}: {paginTotalItemsCount}
              </p>
            ) : (
              <>
                <p className="font-semibold text-sm leading-6 whitespace-nowrap">
                  {t("number_of_items")}
                </p>
                <Select
                  classNames={{
                    control: (state) =>
                      state.isFocused ? "border-red-600" : "border-grey-300",
                  }}
                  options={pageSizeOptions.map((ps) => ({
                    value: `${ps}`,
                    label: `${ps}`,
                  }))}
                  defaultValue={{ value: `${pageSize}`, label: `${pageSize}` }}
                  onChange={handlePageSizeChange}
                  menuPlacement="top"
                  maxMenuHeight={100} // if rows are less the menu gets overlapped and hidden, setting a max height fixes it
                  key={pageSize}
                  styles={{
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      backgroundColor: state.isSelected
                        ? colors.dark
                        : state.isFocused
                          ? colors.fade
                          : "#fff",
                    }),
                  }}
                />
              </>
            )}
          </div>
          {!!paginCurrentPage &&
            !!paginItemsOnCurrentPage &&
            !!paginTotalItemsCount && (
              <p className="hidden lg:block text-xs text-gray-400">
                {t("showing_x_to_y_of_z_records", {
                  start: pageSize * (paginCurrentPage - 1) + 1,
                  end:
                    pageSize * (paginCurrentPage - 1) + paginItemsOnCurrentPage,
                  total: paginTotalItemsCount,
                })}
              </p>
            )}
        </>
      )}
      <ReactPaginate
        onPageChange={handlePageClick}
        pageCount={totalPages}
        forcePage={forcePage}
        pageRangeDisplayed={pageRangeDisplayed}
        marginPagesDisplayed={marginPagesDisplayed}
        containerClassName={`flex overflow-auto lg:justify-center lg:flex-wrap gap-${size} items-center ${size === 1 ? "text-xs" : "text-sm"} mt-7 ms-4 lg:m-0 `}
        pageClassName={`border-2 border-gray-200 rounded-lg hover:border-accent-blue-dark`}
        pageLinkClassName={`block px-${size * 2} py-${size} text-gray-600`}
        activeClassName={`bg-accent-blue-pale text-accent-blue-dark cursor-default pointer-events-none`}
        activeLinkClassName={`cursor-default pointer-events-none`}
        previousClassName={`border-2 border-gray-200 rounded-lg hover:border-accent-blue-dark`}
        previousLinkClassName={`block px-${size * 2} py-${size} text-gray-600`}
        nextClassName={`border-2 border-gray-200 rounded-lg hover:border-accent-blue-dark`}
        nextLinkClassName={`block px-${size * 2} py-${size} text-gray-600`}
        disabledClassName={`text-gray-400 opacity-50 pointer-events-none`}
        disabledLinkClassName={`cursor-default pointer-events-none`}
        breakLabel="..."
        previousLabel="Prev"
        nextLabel="Next"
        renderOnZeroPageCount={null}
      />
    </div>
  );
};

export default Pagination;
